import { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Checkbox
} from '@material-ui/core';
import PlusIcon from '../../icons/Plus';
import TrashIcon from '../../icons/Trash';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ChevronRightIcon from '../../icons/ChevronRight';
import EditIcon from '@material-ui/icons/Edit';
import { questionnaireApi } from '../../api/questionnaireApi';
import useSettings from '../../hooks/useSettings';
import toast from 'react-hot-toast';
import Scrollbar from '../../components/Scrollbar';

const QuestionnaireDetails = () => {
  const { settings } = useSettings();
  const { id } = useParams();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  
  // Question form state
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState('open'); // 'open' or 'mcq'
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [points, setPoints] = useState(0);
  const [correctAnswer, setCorrectAnswer] = useState('');

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = window.localStorage.getItem('accessToken');
      const response = await questionnaireApi.getQuestions(id, accessToken);
      if (response.status) {
        // Parse options if they are stored as JSON string
        const parsed = response.data.map(q => ({
          ...q,
          options: typeof q.options === 'string' ? JSON.parse(q.options) : (q.options || [])
        }));
        setQuestions(parsed);
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du chargement des questions');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadQuestionnaire = useCallback(async () => {
    try {
      const accessToken = window.localStorage.getItem('accessToken');
      const response = await questionnaireApi.getQuestionnaireById(id, accessToken);
      if (response.status) {
        setQuestionnaire(response.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  useEffect(() => {
    loadQuestions();
    loadQuestionnaire();
  }, [loadQuestions, loadQuestionnaire]);

  const handleOpenModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionText(question.text);
      setQuestionType(question.type);
      setOptions(question.options || []);
      setPoints(question.points || 0);
      setCorrectAnswer(question.correct_answer || '');
    } else {
      setEditingQuestion(null);
      setQuestionText('');
      setQuestionType('open');
      setOptions([]);
      setPoints(0);
      setCorrectAnswer('');
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewOption('');
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      if (questionnaire?.type === 'Satisfaction') {
        setOptions([...options, { text: newOption.trim(), weight: 0 }]);
      } else {
        setOptions([...options, newOption.trim()]);
      }
      setNewOption('');
    }
  };

  const handleRemoveOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      toast.error('Veuillez saisir le texte de la question');
      return;
    }

    if (questionType === 'mcq' && options.length < 2) {
      toast.error('Une question à choix multiple doit avoir au moins 2 options');
      return;
    }

    try {
      const accessToken = window.localStorage.getItem('accessToken');
      const payload = {
        questionnaire_id: id,
        text: questionText,
        type: questionType,
        options: questionType === 'mcq' ? options : null,
        sort_order: editingQuestion ? editingQuestion.sort_order : questions.length,
        points: parseInt(points, 10) || 0,
        correct_answer: questionType === 'mcq' ? correctAnswer : null
      };

      let response;
      if (editingQuestion) {
        response = await questionnaireApi.updateQuestion({ ...payload, id: editingQuestion.id }, accessToken);
      } else {
        response = await questionnaireApi.createQuestion(payload, accessToken);
      }

      if (response.status) {
        toast.success(editingQuestion ? 'Question mise à jour' : 'Question ajoutée');
        handleCloseModal();
        loadQuestions();
      } else {
        toast.error(response.message || 'Une erreur est survenue');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (questionId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const response = await questionnaireApi.deleteQuestion(questionId, accessToken);
        if (response.status) {
          toast.success('Question supprimée');
          loadQuestions();
        }
      } catch (err) {
        console.error(err);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Dashboard: Questions | Apteed</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 8
        }}
      >
        <Container maxWidth={settings.compact ? 'xl' : false}>
          <Grid
            container
            justifyContent="space-between"
            spacing={3}
          >
            <Grid item>
              <Typography
                color="textPrimary"
                variant="h5"
              >
                Questions du questionnaire
              </Typography>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightIcon fontSize="small" />}
                sx={{ mt: 1 }}
              >
                <Link
                  color="textPrimary"
                  component={RouterLink}
                  to="/dashboard"
                  variant="subtitle2"
                >
                  Tableau de bord
                </Link>
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                >
                  Qualité
                </Typography>
                <Link
                  color="textPrimary"
                  component={RouterLink}
                  to="/dashboard/questionnaire-formation"
                  variant="subtitle2"
                >
                  Questionnaires
                </Link>
                <Typography
                  color="textSecondary"
                  variant="subtitle2"
                >
                  Questions
                </Typography>
              </Breadcrumbs>
            </Grid>
            <Grid item>
              <Box sx={{ m: -1 }}>
                <Button
                  color="primary"
                  startIcon={<ArrowBackIcon fontSize="small" />}
                  component={RouterLink}
                  to="/dashboard/questionnaire-formation"
                  sx={{ mr: 1 }}
                >
                  Retour
                </Button>
                <Button
                  color="primary"
                  startIcon={<PlusIcon fontSize="small" />}
                  variant="contained"
                  onClick={() => handleOpenModal()}
                >
                  Ajouter une question
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Card>
              <Scrollbar>
                <Box sx={{ minWidth: 700 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Question</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Options</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {questions.map((q) => (
                        <TableRow
                          hover
                          key={q.id}
                        >
                          <TableCell>
                            <Typography
                              color="textPrimary"
                              variant="subtitle2"
                            >
                              {q.text}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {q.type === 'mcq' ? 'Choix multiples' : 'Question ouverte'}
                          </TableCell>
                          <TableCell>
                            {q.type === 'mcq' ? (
                              <Typography variant="body2" color="textSecondary">
                                {q.options.map(opt => typeof opt === 'object' ? opt.text : opt).join(', ')}
                              </Typography>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                <Tooltip title="Modifier">
                                    <IconButton
                                        onClick={() => handleOpenModal(q)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                    <IconButton
                                        onClick={() => handleDelete(q.id)}
                                    >
                                        <TrashIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {questions.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="subtitle2" sx={{ py: 3 }}>
                              Aucune question trouvée. Cliquez sur "Ajouter une question" pour commencer.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Scrollbar>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Modal Ajout/Modification Question */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingQuestion ? 'Modifier la question' : 'Ajouter une nouvelle question'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Texte de la question"
              variant="outlined"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              sx={{ mb: 3 }}
            />
            
            <Box display="flex" gap={2} mb={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Type de question</InputLabel>
                <Select
                  value={questionType}
                  onChange={(e) => setQuestionType(e.target.value)}
                  label="Type de question"
                >
                  <MenuItem value="open">Question ouverte (Texte libre)</MenuItem>
                  <MenuItem value="mcq">Choix multiples (QCM)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Points attribués"
                type="number"
                variant="outlined"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
              />
            </Box>

            {questionType === 'mcq' && (
              <Box sx={{ border: '1px solid #eee', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Options de réponse
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nouvelle option"
                    variant="outlined"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleAddOption}
                  >
                    Ajouter
                  </Button>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Sélectionnez la ou les bonne(s) réponse(s) en cochant les cases correspondantes. 
                  (Le point sera attribué uniquement si TOUTES les bonnes réponses sont cochées et aucune mauvaise).
                </Typography>
                <List dense>
                  {options.map((opt, index) => {
                    const isObject = typeof opt === 'object';
                    const optText = isObject ? opt.text : opt;
                    const getCorrectAnswers = (ans) => {
                      if (!ans) return [];
                      try {
                        const parsed = JSON.parse(ans);
                        return Array.isArray(parsed) ? parsed : [ans];
                      } catch (e) {
                        return ans.split(',');
                      }
                    };
                    const correctAnswers = getCorrectAnswers(correctAnswer);
                    const isChecked = correctAnswers.includes(optText);

                    return (
                      <ListItem key={index} sx={{ py: 1, borderBottom: '1px solid #f8f9fa' }}>
                        <ListItemIcon>
                          <Checkbox 
                            checked={isChecked} 
                            onChange={(e) => {
                              let newCorrect;
                              if (e.target.checked) {
                                newCorrect = [...correctAnswers, optText];
                              } else {
                                newCorrect = correctAnswers.filter(a => a !== optText);
                              }
                              setCorrectAnswer(JSON.stringify(newCorrect));
                            }} 
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={optText} 
                          secondary={isObject ? `Poids: ${opt.weight}%` : null}
                        />
                        
                        {isObject && (
                          <TextField
                              label="Poids (%)"
                              type="number"
                              size="small"
                              value={opt.weight}
                              onChange={(e) => {
                                  const newOpts = [...options];
                                  newOpts[index].weight = parseInt(e.target.value, 10) || 0;
                                  setOptions(newOpts);
                              }}
                              sx={{ width: 100, mr: 2 }}
                          />
                        )}

                        <ListItemSecondaryAction>
                          <IconButton size="small" onClick={() => handleRemoveOption(index)}>
                            <TrashIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                  {options.length === 0 && (
                    <Typography variant="body2" color="textSecondary" align="center">
                      Ajoutez au moins 2 options pour le QCM.
                    </Typography>
                  )}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal}>Annuler</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSubmit}
          >
            {editingQuestion ? 'Mettre à jour' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuestionnaireDetails;
